import { useConfirm } from "material-ui-confirm";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAppContext } from "../../../context/AuthContext";
import { ArticleController, ArticleControllerType } from "../../../controller/article/ArticleController";
import LabelsContext from "../../../lang/LabelsContext";
import MessageType from "../../../utils/MessageType";
import { handleErrorMessage, richTextStateFromHTML, richTextStateToHtml, richTextValue } from "../../../utils/Utils";

const numberArray = [0, 1, 2, 3, 4];

interface ArticleReviwerServiceType {
  article: any;
  setReviewersCount: Function;
  numberArray: number[];
  active: number | undefined;
  AddReviewer: Function;
  DeleteReviewer: Function;
  getMailSuggestionToReviewer: Function;
  addedReviewers: any;
  reviewers: any;
  sendMailToAllReviewers: any;
  sendMailToReviewer: any;
  handleOpenDialog: any;
  handleCloseDialog: any;
  openDialog: any;
  mailSuggestion: any;
  handleChangeAccordian: any;
  expanded: any;
  confirmSendToAssistant: any;
  mailRef: any;
  assignSameReviwers: any;
}

export default function ArticleReviwerService({ article }: any): ArticleReviwerServiceType {
  const { showMessage, authData, setBlockUI } = useAppContext();
  const { Labels } = LabelsContext();
  const {
    axiosSetReviewersCount,
    axiosGetAddedReviewerList,
    axiosAddReviewer,
    axiosDeleteReviewer,
    axiosGetMailSuggestionToReviewer,
    axiosSendMailToAllReviewers,
    axiosGetReviewersStatistics,
    axiosSendMailToReviewer,
    axiosPostReturnToAssistant,
    axiosPostAssignSameReviewers,
  }: ArticleControllerType = ArticleController();
  const [mailSuggestion, setMailSuggestion] = useState<any>(null);
  const [reviewers, setReviewers] = useState<any>([]);
  const [addedReviewers, setAddedReviewers] = useState<any>([]);
  const [expanded, setExpanded] = useState<any>({
    qualityOfResearch: true,
    recommendation: false,
    otherNotes: false,
    comments: false,
  });
  const [active, setActive] = useState<number>(-1);
  const [openDialog, setOpenDialog] = useState(false);
  const mailToReviewerId = useRef(null);

  const history = useHistory();

  const confirm = useConfirm();

  const mailRef = useRef<any>();

  useEffect(() => {
    if (article?.id) {
      setActive(article.reviewerRequired);
      getAddedReviewerList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article]);

  const handleChangeAccordian = (name: any) => (event: any, newExpanded: any) => {
    setExpanded({ ...expanded, [name]: !expanded[name] });
  };

  const handleOpenDialog = (reviewerId: any) => {
    mailToReviewerId.current = reviewerId;
    getMailSuggestionToReviewer(reviewerId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    mailToReviewerId.current = null;
    setMailSuggestion(null);
    setOpenDialog(false);
  };

  const AddReviewer = (reviewerId: number) => {
    axiosAddReviewer(article.id, reviewerId)
      .then((result: any) => {
        getAddedReviewerList();
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const DeleteReviewer = (reviewerId: number) => {
    axiosDeleteReviewer(article.id, reviewerId)
      .then((result: any) => {
        getAddedReviewerList();
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const returnToAssistantEditor = () => {
    setBlockUI(true);
    axiosPostReturnToAssistant(article.id)
      .then((result: any) => {
        showMessage("Article is returned to assistant editor.", MessageType.SUCCESS);
        history.push("/toDoList");
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      })
      .finally(() => setBlockUI(false));
  };

  const assignSameReviwers = () => {
    setBlockUI(true);
    axiosPostAssignSameReviewers(article.id)
      .then((response: any) => changeAddedReviwerList(response.data))
      .catch((error: any) => handleErrorMessage(showMessage, error))
      .finally(() => setBlockUI(false));
  };

  const getAddedReviewerList = () => {
    axiosGetAddedReviewerList(article.id)
      .then((result: any) => changeAddedReviwerList(result.data))
      .catch((error: any) => handleErrorMessage(showMessage, error));
  };

  const changeAddedReviwerList = (array: any) => {
    const mapData = array
      .filter((x: any) => x.revision === article.revision)
      .map((x: any) => ({
        ...x,
        fullName: x.reviewer.firstName + " " + x.reviewer.lastName,
      }));
    setAddedReviewers(mapData);
    getReviewersStatistics(mapData);
  };

  const getReviewersStatistics = (added: any) => {
    axiosGetReviewersStatistics()
      .then((result: any) => {
        const mapData = result.data.map((x: any) => ({
          ...x,
          fullName: x.firstName + " " + x.lastName,
        }));
        const filterData = mapData.filter(
          (d: any) => !added.some((a: any) => d.fullName === `${a.reviewer.firstName} ${a.reviewer.lastName}`)
        );
        setReviewers(filterData);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const setReviewersCount = (count: number) => {
    axiosSetReviewersCount(article.id, count)
      .then((result: any) => {
        setActive(count);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const getMailSuggestionToReviewer = (reviewId: number) => {
    axiosGetMailSuggestionToReviewer(article.id, reviewId)
      .then((result: any) => {
        setMailSuggestion(result.data);
        mailRef.current.set(richTextStateFromHTML(result.data.text));
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const sendMailToAllReviewers = () => {
    axiosSendMailToAllReviewers(article.id)
      .then((result: any) => getAddedReviewerList())
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const sendMailToReviewer = (email: any) => {
    const emailDto = {
      from: authData?.user.email,
      replyTo: authData?.user.email,
      to: email.mailto.length > 0 ? email.mailto.join() : null,
      sentDate: new Date(),
      subject: email.subject,
      text: richTextStateToHtml(richTextValue(mailRef)),
    };
    axiosSendMailToReviewer(article.id, mailToReviewerId.current, emailDto)
      .then((result: any) => {
        handleCloseDialog();
        getAddedReviewerList();
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const confirmSendToAssistant = () => {
    return confirm({
      description: "Are you sure you want forward paper to Assistant editor?",
      confirmationText: Labels.YES,
      cancellationText: Labels.NO,
      confirmationButtonProps: {
        variant: "contained",
        color: "primary",
      },
    })
      .then(returnToAssistantEditor)
      .catch(() => {});
  };

  return {
    article,
    setReviewersCount,
    numberArray,
    active,
    AddReviewer,
    DeleteReviewer,
    getMailSuggestionToReviewer,
    addedReviewers,
    reviewers,
    sendMailToAllReviewers,
    sendMailToReviewer,
    handleOpenDialog,
    handleCloseDialog,
    openDialog,
    mailSuggestion,
    handleChangeAccordian,
    expanded,
    confirmSendToAssistant,
    mailRef,
    assignSameReviwers,
  };
}
export type { ArticleReviwerServiceType };
